import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import './InventoryManager.css';
import AppIcon from './icons/AppIcon';

const InventoryManager = () => {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estoqueProdutos, setEstoqueProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [novaCategoria, setNovaCategoria] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem('inventoryViewMode') || 'grid';
  });
  const [filtros, setFiltros] = useState({
    busca: '',
    categoria: '',
    status: 'ATIVO',
    baixoEstoque: false,
  });
  const [showModal, setShowModal] = useState(false);
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState(null);
  const [produtoAjuste, setProdutoAjuste] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [ajuste, setAjuste] = useState({
    tipo: 'ENTRADA',
    quantidade: '',
    custo_unitario: '',
    observacao: '',
  });

  const [form, setForm] = useState({
    nome: '',
    categoria_id: '',
    sku: '',
    codigo_barras: '',
    unidade_base: 'UN',
    descricao: '',
    estoque_atual: 0,
    estoque_minimo: 0,
    estoque_maximo: 0,
    custo_unitario: '',
    fornecedor_id: '',
    status: 'ATIVO',
  });

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('inventoryViewMode', viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    if (companyId) {
      loadEstoqueProdutos(companyId);
    }
  }, [filtros]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.company_id) {
        setError('Usuário não vinculado a empresa');
        return;
      }

      setCompanyId(profile.company_id);
      await Promise.all([
        loadCategorias(profile.company_id),
        loadFornecedores(profile.company_id),
        loadEstoqueProdutos(profile.company_id),
      ]);
    } catch (err) {
      console.error('Erro ao carregar estoque:', err);
      setError('Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
  };

  const loadCategorias = async (cid) => {
    const { data, error } = await supabase
      .from('categorias_estoque')
      .select('*')
      .eq('company_id', cid)
      .eq('ativo', true)
      .order('nome', { ascending: true });

    if (!error && data) {
      setCategorias(data);
    }
  };

  const loadFornecedores = async (cid) => {
    const { data, error } = await supabase
      .from('fornecedores')
      .select('id, nome_fantasia, segmento_atendido, status')
      .eq('company_id', cid)
      .order('nome_fantasia', { ascending: true });

    if (!error && data) {
      setFornecedores(data);
    }
  };

  const handleAdicionarCategoria = async () => {
    const nome = novaCategoria.trim();
    if (!nome || !companyId) return;

    try {
      const { data, error } = await supabase
        .from('categorias_estoque')
        .insert({
          company_id: companyId,
          nome,
          ativo: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('id, nome')
        .single();

      if (error) throw error;

      setCategorias((prev) => [...prev, data]);
      setForm((prev) => ({ ...prev, categoria_id: data.id }));
      setNovaCategoria('');
      setSuccess('Categoria criada com sucesso!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Erro ao criar categoria:', err);
      setError('Erro ao criar categoria');
    }
  };

  const loadEstoqueProdutos = async (cid) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('estoque_produtos')
        .select('*')
        .eq('company_id', cid);

      if (filtros.status) {
        query = query.eq('status', filtros.status);
      }

      if (filtros.categoria) {
        query = query.eq('categoria_id', filtros.categoria);
      }

      if (filtros.busca) {
        query = query.ilike('nome', `%${filtros.busca}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const filtrados = filtros.baixoEstoque
        ? data.filter((item) => Number(item.estoque_atual || 0) <= Number(item.estoque_minimo || 0))
        : data;

      setEstoqueProdutos(filtrados || []);
    } catch (err) {
      console.error('Erro ao carregar itens de estoque:', err);
      setError('Erro ao carregar itens de estoque');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      nome: '',
      categoria_id: '',
      sku: '',
      codigo_barras: '',
      unidade_base: 'UN',
      descricao: '',
      estoque_atual: 0,
      estoque_minimo: 0,
      estoque_maximo: 0,
      custo_unitario: '',
      fornecedor_id: '',
      status: 'ATIVO',
    });
    setNovaCategoria('');
  };

  const handleNovoItem = () => {
    resetForm();
    setProdutoEditando(null);
    setShowModal(true);
  };

  const handleEditarItem = (produto) => {
    setForm({
      nome: produto.nome || '',
      categoria_id: produto.categoria_id || '',
      sku: produto.sku || '',
      codigo_barras: produto.codigo_barras || '',
      unidade_base: produto.unidade_base || 'UN',
      descricao: produto.descricao || '',
      estoque_atual: produto.estoque_atual ?? 0,
      estoque_minimo: produto.estoque_minimo ?? 0,
      estoque_maximo: produto.estoque_maximo ?? 0,
      custo_unitario: produto.custo_unitario ?? '',
      fornecedor_id: produto.fornecedor_id || '',
      status: produto.status || 'ATIVO',
    });
    setProdutoEditando(produto);
    setShowModal(true);
  };

  const handleSalvarItem = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);

      if (!companyId) throw new Error('Empresa não encontrada');

      const payload = {
        company_id: companyId,
        nome: form.nome,
        categoria_id: form.categoria_id || null,
        sku: form.sku || null,
        codigo_barras: form.codigo_barras || null,
        unidade_base: form.unidade_base || 'UN',
        descricao: form.descricao || null,
        estoque_atual: Number(form.estoque_atual || 0),
        estoque_minimo: Number(form.estoque_minimo || 0),
        estoque_maximo: Number(form.estoque_maximo || 0),
        custo_unitario: form.custo_unitario ? Number(form.custo_unitario) : null,
        fornecedor_id: form.fornecedor_id || null,
        status: form.status || 'ATIVO',
        updated_at: new Date().toISOString(),
      };

      if (produtoEditando?.id) {
        const { error } = await supabase
          .from('estoque_produtos')
          .update(payload)
          .eq('id', produtoEditando.id)
          .eq('company_id', companyId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('estoque_produtos')
          .insert(payload);

        if (error) throw error;
      }

      setSuccess('Item salvo com sucesso!');
      setShowModal(false);
      loadEstoqueProdutos(companyId);
    } catch (err) {
      console.error('Erro ao salvar item:', err);
      setError('Erro ao salvar item');
    }
  };

  const handleAbrirAjuste = (produto) => {
    setProdutoAjuste(produto);
    setAjuste({
      tipo: 'ENTRADA',
      quantidade: '',
      custo_unitario: produto?.custo_unitario ?? '',
      observacao: '',
    });
    setShowAjusteModal(true);
  };

  const handleSalvarAjuste = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);

      if (!companyId || !produtoAjuste?.id) {
        throw new Error('Produto inválido');
      }

      const quantidade = Number(ajuste.quantidade || 0);
      const custoUnitario = ajuste.custo_unitario ? Number(ajuste.custo_unitario) : null;
      const estoqueAtual = Number(produtoAjuste.estoque_atual || 0);

      let novoEstoque = estoqueAtual;
      if (ajuste.tipo === 'ENTRADA') novoEstoque += quantidade;
      if (ajuste.tipo === 'SAIDA') novoEstoque -= quantidade;
      if (ajuste.tipo === 'AJUSTE') novoEstoque = quantidade;

      const { error: updateError } = await supabase
        .from('estoque_produtos')
        .update({
          estoque_atual: novoEstoque,
          custo_unitario: custoUnitario ?? produtoAjuste.custo_unitario,
          updated_at: new Date().toISOString(),
        })
        .eq('id', produtoAjuste.id)
        .eq('company_id', companyId);

      if (updateError) throw updateError;

      await supabase.from('estoque_movimentacoes').insert({
        company_id: companyId,
        estoque_produto_id: produtoAjuste.id,
        tipo: ajuste.tipo,
        quantidade,
        custo_unitario: custoUnitario,
        origem: 'manual',
        referencia_id: null,
        created_at: new Date().toISOString(),
      });

      setSuccess('Estoque atualizado com sucesso!');
      setShowAjusteModal(false);
      loadEstoqueProdutos(companyId);
    } catch (err) {
      console.error('Erro ao ajustar estoque:', err);
      setError('Erro ao ajustar estoque');
    }
  };

  if (loading) {
    return (
      <div className="inventory-manager">
        <LoadingSpinner message="Carregando estoque..." />
      </div>
    );
  }

  return (
    <div className="inventory-manager">
      <div className="inventory-header-bar">
        <div>
          <h2>Estoque</h2>
          <p>Gerencie insumos e itens de estoque da sua empresa.</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn-icon view-toggle"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            title={viewMode === 'grid' ? 'Visualizar em lista' : 'Visualizar em cards'}
          >
            <AppIcon name={viewMode === 'grid' ? 'view_list' : 'grid_view'} />
          </button>
          <button className="btn-secondary" type="button" onClick={loadData}>
            <AppIcon name="refresh" />
            Atualizar
          </button>
          <button className="btn-primary" type="button" onClick={handleNovoItem}>
            <AppIcon name="add" />
            Novo Item
          </button>
        </div>
      </div>

      <div className="inventory-filters">
        <div className="filter-group">
          <AppIcon name="search" />
          <input
            type="text"
            placeholder="Buscar item..."
            value={filtros.busca}
            onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
          />
        </div>
        <select
          value={filtros.categoria}
          onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}
        >
          <option value="">Todas as categorias</option>
          {categorias.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.nome}</option>
          ))}
        </select>
        <select
          value={filtros.status}
          onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
        >
          <option value="ATIVO">Ativo</option>
          <option value="INATIVO">Inativo</option>
          <option value="RASCUNHO">Rascunho</option>
        </select>
        <label className="checkbox-filter">
          <input
            type="checkbox"
            checked={filtros.baixoEstoque}
            onChange={(e) => setFiltros({ ...filtros, baixoEstoque: e.target.checked })}
          />
          Abaixo do mínimo
        </label>
      </div>

      {error && (
        <div className="alert alert-error">
          <AppIcon name="error" />
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <AppIcon name="check_circle" />
          {success}
        </div>
      )}

      {estoqueProdutos.length === 0 ? (
        <div className="empty-state">
          <AppIcon name="inventory" />
          <h3>Nenhum item de estoque cadastrado</h3>
          <p>Clique em "Novo Item" para começar</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="inventory-grid">
          {estoqueProdutos.map((produto) => (
            <div key={produto.id} className="inventory-card">
              <div className="inventory-card-header">
                <div>
                  <h3>{produto.nome}</h3>
                  <p>{categorias.find((cat) => cat.id === produto.categoria_id)?.nome || 'Sem categoria'}</p>
                </div>
                <span className={`status-badge status-${(produto.status || 'ativo').toLowerCase()}`}>
                  {produto.status || 'ATIVO'}
                </span>
              </div>

              <div className="inventory-card-body">
                <div className="inventory-metrics">
                  <div>
                    <span>Estoque atual</span>
                    <strong>{Number(produto.estoque_atual || 0)}</strong>
                  </div>
                  <div>
                    <span>Min / Máx</span>
                    <strong>{Number(produto.estoque_minimo || 0)} / {Number(produto.estoque_maximo || 0)}</strong>
                  </div>
                  <div>
                    <span>Custo</span>
                    <strong>R$ {Number(produto.custo_unitario || 0).toFixed(2)}</strong>
                  </div>
                </div>

                <div className="inventory-meta">
                  <span>Unidade: {produto.unidade_base || 'UN'}</span>
                  {produto.sku && <span>SKU: {produto.sku}</span>}
                </div>
              </div>

              <div className="inventory-actions">
                <button className="btn-icon" onClick={() => handleEditarItem(produto)} title="Editar">
                  <AppIcon name="edit" />
                </button>
                <button className="btn-icon" onClick={() => handleAbrirAjuste(produto)} title="Ajustar estoque">
                  <AppIcon name="tune" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="inventory-list">
          <div className="inventory-list-header">
            <span>Item</span>
            <span>Categoria</span>
            <span>Estoque</span>
            <span>Custo</span>
            <span>Status</span>
            <span>Ações</span>
          </div>
          {estoqueProdutos.map((produto) => (
            <div key={produto.id} className="inventory-list-row">
              <div className="inventory-list-main">
                <strong>{produto.nome}</strong>
                <span>{produto.unidade_base || 'UN'} {produto.sku ? `• SKU ${produto.sku}` : ''}</span>
              </div>
              <span>{categorias.find((cat) => cat.id === produto.categoria_id)?.nome || 'Sem categoria'}</span>
              <span>
                {Number(produto.estoque_atual || 0)}
                <small>Min {Number(produto.estoque_minimo || 0)} / Máx {Number(produto.estoque_maximo || 0)}</small>
              </span>
              <span>R$ {Number(produto.custo_unitario || 0).toFixed(2)}</span>
              <span className={`status-badge status-${(produto.status || 'ativo').toLowerCase()}`}>
                {produto.status || 'ATIVO'}
              </span>
              <div className="inventory-actions">
                <button className="btn-icon" onClick={() => handleEditarItem(produto)} title="Editar">
                  <AppIcon name="edit" />
                </button>
                <button className="btn-icon" onClick={() => handleAbrirAjuste(produto)} title="Ajustar estoque">
                  <AppIcon name="tune" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content inventory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{produtoEditando ? 'Editar Item' : 'Novo Item de Estoque'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <AppIcon name="close" />
              </button>
            </div>

            <form className="modal-body" onSubmit={handleSalvarItem}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Código de Barras</label>
                  <input
                    type="text"
                    value={form.codigo_barras}
                    onChange={(e) => setForm({ ...form, codigo_barras: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Categoria</label>
                  <select
                    value={form.categoria_id}
                    onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}
                  >
                    <option value="">Selecione...</option>
                    {categorias.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  </select>
                  <div className="inline-category">
                    <input
                      type="text"
                      placeholder="Nova categoria"
                      value={novaCategoria}
                      onChange={(e) => setNovaCategoria(e.target.value)}
                    />
                    <button type="button" className="btn-secondary" onClick={handleAdicionarCategoria}>
                      Adicionar
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unidade base</label>
                  <input
                    type="text"
                    value={form.unidade_base}
                    onChange={(e) => setForm({ ...form, unidade_base: e.target.value })}
                    placeholder="UN, KG, L..."
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="ATIVO">Ativo</option>
                    <option value="INATIVO">Inativo</option>
                    <option value="RASCUNHO">Rascunho</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  rows="3"
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estoque atual</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.estoque_atual}
                    onChange={(e) => setForm({ ...form, estoque_atual: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Estoque mínimo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.estoque_minimo}
                    onChange={(e) => setForm({ ...form, estoque_minimo: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estoque máximo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.estoque_maximo}
                    onChange={(e) => setForm({ ...form, estoque_maximo: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Custo unitário</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.custo_unitario}
                    onChange={(e) => setForm({ ...form, custo_unitario: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Fornecedor</label>
                <select
                  value={form.fornecedor_id}
                  onChange={(e) => setForm({ ...form, fornecedor_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {fornecedores.map((fornecedor) => (
                    <option key={fornecedor.id} value={fornecedor.id}>
                      {fornecedor.nome_fantasia} {fornecedor.segmento_atendido ? `(${fornecedor.segmento_atendido})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {produtoEditando ? 'Salvar Alterações' : 'Cadastrar Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAjusteModal && (
        <div className="modal-overlay" onClick={() => setShowAjusteModal(false)}>
          <div className="modal-content inventory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajustar estoque</h3>
              <button className="btn-close" onClick={() => setShowAjusteModal(false)}>
                <AppIcon name="close" />
              </button>
            </div>

            <form className="modal-body" onSubmit={handleSalvarAjuste}>
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo</label>
                  <select
                    value={ajuste.tipo}
                    onChange={(e) => setAjuste({ ...ajuste, tipo: e.target.value })}
                  >
                    <option value="ENTRADA">Entrada</option>
                    <option value="SAIDA">Saída</option>
                    <option value="AJUSTE">Ajuste manual</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantidade</label>
                  <input
                    type="number"
                    step="0.01"
                    value={ajuste.quantidade}
                    onChange={(e) => setAjuste({ ...ajuste, quantidade: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Custo unitário</label>
                  <input
                    type="number"
                    step="0.01"
                    value={ajuste.custo_unitario}
                    onChange={(e) => setAjuste({ ...ajuste, custo_unitario: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Observação</label>
                  <input
                    type="text"
                    value={ajuste.observacao}
                    onChange={(e) => setAjuste({ ...ajuste, observacao: e.target.value })}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAjusteModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Aplicar Ajuste
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManager;
